import { absoluteURL, canUseDOM } from '@ps/web-core-utils';
import type { Html } from '@ps/web-martech-cms-types';
import { useTrackers } from '@ps/web-shared-analytics';
import { RootHistoryContext } from '@ps/web-shared-components';
import classNames from 'clsx';
import type { History } from 'history';
import { get as lodashGet, upperFirst } from 'lodash';
import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';
import { routeHandler } from '@swf/core/utils/c2l';

import useAuthenticatedContent from '../../hooks/useAuthenticatedContent';
import usePostDownloadNavigation from '../../hooks/usePostDownloadNavigation';
import { ContainerVariantContext } from '../CMSContainer/CMSContainer';

import styles from './CMSHTML.scss';

interface Props extends Html {
  className?: string;
  tag?: string;
  preventClick?: boolean;
  logged_in_content?: string;
}

const hrefMatch = /href=(["'])(\/[^/][^"']*)["']/g;
const noEmbeddedHrefMatch = /href=(["'])(\/(?!embedded)[^/][^"']*)["']/g;
const externalLinkMatch = /href=["'](\/[^][^"']*)["']/gm;

const CMSHTML: React.FC<Props> = ({
  content: HTMLContent,
  logged_in_content: loggedInContent,
  tag = 'div',
  className,
  preventClick = false
}) => {
  const { modal } = useDispatch<Dispatch>();
  const history = useContext(RootHistoryContext as React.Context<History<unknown> | null>);
  const origin = useSelector(select.location.getOrigin);
  const localePrefix = useSelector(select.location.getLocaleURL);
  const baseUrl = useSelector(select.location.getBaseURL) || '';
  const isEmbeddedRoute = useSelector(select.location.isEmbeddedRoute);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const locale = useSelector(select.locale.get);
  const { buttonPressed, linkClicked } = useTrackers();
  const postDownloadNavigate = usePostDownloadNavigation();
  const { containerVariant } = useContext(ContainerVariantContext);

  let content = useAuthenticatedContent(HTMLContent, loggedInContent);

  if (!content) return null;

  if (isEmbedded) {
    const replacer = (_: string, path: string) => `href="external-${origin}${localePrefix}${path}"`;

    content = content
      .split('<')
      .map((link) =>
        link.includes('class="external"') ? link.replace(externalLinkMatch, replacer) : link
      )
      .join('<');
  }

  if (isEmbeddedRoute) {
    content = content.replace(noEmbeddedHrefMatch, `href=$1/embedded$2$1`);
  }

  if (localePrefix) {
    content = content.replace(hrefMatch, `href=$1${localePrefix}$2$1`);
  }

  const trackClick: React.MouseEventHandler = (event) => {
    const button = (event.target as HTMLElement).closest('button');
    let trackId =
      button?.getAttribute('data-track-id') || (locale === 'en' ? button?.innerText : undefined);

    if (button) {
      buttonPressed({ buttonName: trackId });
    }
    const a = (event.target as HTMLElement).closest('a');
    trackId = a?.getAttribute('data-track-id') || (locale === 'en' ? a?.innerText : undefined);

    if (a) {
      const href = a.getAttribute('href');
      linkClicked({
        destinationUrl: href || '',
        linkText: trackId
      });
    }
  };

  const onClick: React.MouseEventHandler = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    if (preventClick) {
      return;
    }

    trackClick(event);
    const anchorElement = (event.target as HTMLElement).closest('a');
    if (!anchorElement) return;
    const href = anchorElement.getAttribute('href');
    postDownloadNavigate(href);
    if (history && routeHandler(href, history, event)) {
      return;
    }
    if (isEmbedded && anchorElement.hasAttribute('c2lEmbedded') && href) {
      event.preventDefault();
      canUseDOM && lodashGet(window, ['starsweb', 'app', 'execute'], () => undefined)(href);
      return;
    }
    if (
      href &&
      !absoluteURL(href) &&
      !href.startsWith('#') &&
      !anchorElement.hasAttribute('download') &&
      !['_top', '_blank'].includes(anchorElement.getAttribute('target') || '')
    ) {
      if (href.indexOf('#') === -1) {
        event.preventDefault();
      }
      if (anchorElement.hasAttribute('data-hard-link')) {
        window.location.assign(href);
        return;
      }
      history?.push(href.replace(baseUrl, ''));
      modal.close();
    }
  };

  return React.createElement(tag, {
    dangerouslySetInnerHTML: { __html: content },
    className: classNames(
      styles.htmlContent,
      className,
      styles[`table${upperFirst(containerVariant ?? 'default')}`]
    ),
    onClick,
    suppressHydrationWarning: true
  });
};

export default CMSHTML;
